// The fake api's "you" routes, past the console gate in `fake-server.ts`: what the Account page and the
// notifications bell read and change about the signed-in member (spec/13 §2; spec/07 §3.3, §6.7;
// spec/16 §2, §3). None carries a permission — each is about the caller only.
//
//   GET    /v1/me/sessions                                     listMySessions             two devices, this one first
//   DELETE /v1/me/sessions/{sessionId}                         revokeMySession
//   GET    /v1/organizations/{o}/members/me/pins               listMyStaffPins            the venues you work at, set or not
//   PUT    /v1/organizations/{o}/members/me/pin                setMyStaffPin              X-Clavian-Venue required
//   GET    /v1/organizations/{o}/notification-preferences      getNotificationPreferences
//   PUT    /v1/organizations/{o}/notification-preferences      putNotificationPreferences  the daily summary an owner's alone
//   GET    /v1/organizations/{o}/notifications                 listNotifications          empty unless a test adds rows
//   POST   /v1/organizations/{o}/notifications/{id}/read       markNotificationRead
//   POST   /v1/organizations/{o}/notifications/read            markAllNotificationsRead
//
// The staff PIN keeps the contract's rules: six digits, not one repeated nor a run (`400`), and a venue
// in `pinLockedAt` answers `403 staff_pin_locked` until a manager resets it.

import type { Notification, NotificationPreferences, SignedInSession } from '@clavian/api-client';
import { PutNotificationPreferencesSchema, SetStaffPinSchema } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

interface AccountHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string) => FakeResponse;
  readonly organizationId: string;
  /**
   * The venues the member works at — what `GET /v1/me/venues` lists for the organisation: a PIN may be
   * set at these only, and `GET …/members/me/pins` lists exactly these, as the server does.
   */
  readonly venues: () => readonly { readonly id: string; readonly name: string }[];
  /** Whether the member is an owner: only an owner subscribes to the daily summary (`403` otherwise). */
  readonly isOwner: () => boolean;
}

const NOW = '2026-09-22T09:00:00.000Z';

export const MY_SESSIONS = {
  current: 'aus_01k5znw4hmfay8vvr14d2pf8a1',
  phone: 'aus_01k5znw4hmfay8vvr14d2pf8a2',
} as const;

function seedSessions(): SignedInSession[] {
  return [
    {
      id: MY_SESSIONS.current,
      current: true,
      createdAt: '2026-09-22T08:00:00.000Z',
      lastActiveAt: NOW,
      expiresAt: '2026-09-29T08:00:00.000Z',
      ipAddress: '203.0.113.24',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) Chrome/140',
      device: 'Chrome on Windows',
      loginMethod: 'password',
      secondFactor: true,
    },
    {
      id: MY_SESSIONS.phone,
      current: false,
      createdAt: '2026-09-18T19:10:00.000Z',
      lastActiveAt: '2026-09-21T21:40:00.000Z',
      expiresAt: '2026-09-25T19:10:00.000Z',
      ipAddress: '198.51.100.7',
      userAgent: 'Clavian Staff/1.4 (Android 15)',
      device: 'Clavian Staff on Android',
      loginMethod: 'phone-otp',
      secondFactor: false,
    },
  ];
}

/** A notification for the bell, for a test to put in the inbox. */
export function notificationFixture(overrides: Partial<Notification> = {}): Notification {
  return {
    id: 'ntf_01k5znw4hmfay8vvr14d2pf8b1',
    code: 'N-20',
    category: 'floor',
    venueId: null,
    title: 'Station 4 needs help',
    body: 'A player at PC-04 asked for help with their headset.',
    data: {},
    readAt: null,
    createdAt: NOW,
    ...overrides,
  } as Notification;
}

export function createFakeAccount(helpers: AccountHelpers) {
  const { json, problem, organizationId } = helpers;
  const state = {
    sessions: seedSessions(),
    /** Venue id → the PIN set there. */
    pins: new Map<string, string>(),
    /** Venues where five wrong PINs locked it (spec/07 §6.7). */
    pinLockedAt: new Set<string>(),
    preferences: { quietHours: { enabled: false, start: '22:00', end: '08:00' }, overrides: [], mutedVenueIds: [], subscriptions: [] } as NotificationPreferences,
    notifications: [] as Notification[],
  };

  const organizationPath = `/v1/organizations/${organizationId}`;
  const invalid = () => problem(400, 'validation_failed', 'Validation failed');

  function setPin(headers: Readonly<Record<string, string>>, input: Record<string, unknown>): FakeResponse {
    const venueId = headers['x-clavian-venue'];
    if (venueId === undefined || !helpers.venues().some((venue) => venue.id === venueId)) return problem(404, 'not_found', 'No such venue you work at.');
    const parsed = SetStaffPinSchema.safeParse(input);
    if (!parsed.success) return invalid();
    const digits = [...parsed.data.pin].map(Number);
    const steps = digits.slice(1).map((digit, index) => digit - (digits[index] ?? 0));
    if (digits.every((digit) => digit === digits[0]) || steps.every((step) => step === 1) || steps.every((step) => step === -1)) return invalid();
    if (state.pinLockedAt.has(venueId)) return problem(403, 'staff_pin_locked', 'Your PIN here is locked.');
    state.pins.set(venueId, parsed.data.pin);
    return { status: 204, headers: {}, body: '' };
  }

  /** `undefined` for a request that is not one of these routes. */
  function handle(method: string, pathname: string, headers: Readonly<Record<string, string>>, input: Record<string, unknown>): FakeResponse | undefined {
    if (pathname === '/v1/me/sessions' && method === 'GET') return json(200, { data: state.sessions });
    const revoke = /^\/v1\/me\/sessions\/([^/]+)$/.exec(pathname);
    if (revoke !== null && method === 'DELETE') {
      const found = state.sessions.find((session) => session.id === revoke[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such session of yours.');
      state.sessions = state.sessions.filter((session) => session.id !== found.id);
      return { status: 204, headers: {}, body: '' };
    }

    if (!pathname.startsWith(organizationPath)) return undefined;
    const rest = pathname.slice(organizationPath.length);

    if (rest === '/members/me/pins' && method === 'GET') {
      return json(200, {
        data: helpers.venues().map((venue) => ({
          venueId: venue.id,
          venueName: venue.name,
          set: state.pins.has(venue.id) || state.pinLockedAt.has(venue.id),
          locked: state.pinLockedAt.has(venue.id),
        })),
      });
    }
    if (rest === '/members/me/pin' && method === 'PUT') return setPin(headers, input);

    if (rest === '/notification-preferences') {
      if (method === 'GET') return json(200, state.preferences);
      if (method === 'PUT') {
        const parsed = PutNotificationPreferencesSchema.safeParse(input);
        if (!parsed.success) return invalid();
        const asked = parsed.data.subscriptions;
        if (asked !== undefined && asked.length > 0 && !helpers.isOwner()) return problem(403, 'forbidden', 'Only an owner of the organisation receives the daily summary.');
        // A subscription kept keeps when it began; a new one begins now (ADR-0044).
        const subscriptions =
          asked === undefined
            ? state.preferences.subscriptions
            : asked.map((one) => state.preferences.subscriptions.find((kept) => kept.code === one.code && kept.venueId === one.venueId) ?? { ...one, since: NOW });
        state.preferences = {
          quietHours: parsed.data.quietHours ?? state.preferences.quietHours,
          overrides: parsed.data.overrides ?? state.preferences.overrides,
          mutedVenueIds: parsed.data.mutedVenueIds ?? state.preferences.mutedVenueIds,
          subscriptions,
        } as NotificationPreferences;
        return json(200, state.preferences);
      }
    }

    // The inbox (F-NTF-03): the bell reads the caller's own rows, marks one read, or all of them.
    if (rest === '/notifications' && method === 'GET') {
      const rows = [...state.notifications].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      return json(200, { data: rows, page: { cursor: null, hasMore: false } });
    }
    if (rest === '/notifications/read' && method === 'POST') {
      const unread = state.notifications.filter((row) => row.readAt === null).length;
      state.notifications = state.notifications.map((row) => (row.readAt === null ? { ...row, readAt: NOW } : row));
      return json(200, { updated: unread });
    }
    const readOne = /^\/notifications\/([^/]+)\/read$/.exec(rest);
    if (readOne !== null && method === 'POST') {
      const found = state.notifications.find((row) => row.id === readOne[1]);
      if (found === undefined) return problem(404, 'not_found', 'No such notification of yours at this organisation.');
      const read = { ...found, readAt: found.readAt ?? NOW };
      state.notifications = state.notifications.map((row) => (row.id === read.id ? read : row));
      return json(200, read);
    }
    return undefined;
  }

  return { state, handle };
}

export type FakeAccount = ReturnType<typeof createFakeAccount>;
